import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Typography,
  CircularProgress,
} from '@material-ui/core'
import { useFetcher } from 'libs/fetcher'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import SecondaryButton from 'app/components/buttons/SecondaryButton'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import { formatDate } from 'commons/date'
import effects from 'app/effects/effects'
import { usePlan, useSubscription } from 'commons/useSubscription'
import { FuseAnimate } from '@fuse'
import Loader from 'app/components/Loader'
import { pricePerNoti } from 'commons/number'
import PlanPhysicalWebStarterPackInfo from './PlanPhysicalWebStarterPackInfo'

import {
  getPlanType,
  isDowngradePlan,
  isUpgradePlan,
  getExpireDatePlanByPlanType,
  isPlanFreeTierByPlanId,
  isPhysicalWebStarterPackByPlanId,
  RecurringPlanPrice,
} from 'commons/plan'
import moment from 'moment'
import clsx from 'clsx'
import * as R from 'ramda'
import React, { useCallback, useState, useRef } from 'react'
import { makeStyles } from '@material-ui/styles'
import { PlanBillingPaymentMethodCard } from './PlanBillingPaymentMethod'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlaceDetailsInformation } from './PlanBillingDetails.js'
import { useIsPlaceExpired } from 'commons/useIsPlaceRestricted'
import { usePlanBillingContext } from '../usePlanBillingContext'
import ResponsiveDialog from 'app/components/dialog/ResponsiveDialog'
const useStyles = makeStyles((theme) => ({
  summaryBodyContainer: {
    'border-top': `1px solid ${theme.palette.grey.veryLight}`,
    'border-bottom': `1px solid ${theme.palette.grey.veryLight}`,
  },
}))

const PlanActionType = {
  UPGRADE: 'upgraded',
  DOWNGRADE: 'downgraded',
}

const TypographyFormattedMessage = ({ color, variant, className, ...rest }) => {
  return (
    <Typography variant={variant} color={color} className={className}>
      <FormattedMessage {...rest} />
    </Typography>
  )
}

export const PurchaseFailureDialog = ({ isOpen, onClose }) => {
  const { formatMessage } = useIntl()

  return (
    <ConfirmationDialog
      open={isOpen}
      fullWidth
      maxWidth="xs"
      title={
        <span className="text-watermelon">
          <FormattedMessage id="planAndBilling.yourPlan.changePlanFailure.title" />
        </span>
      }
      body={formatMessage(
        {
          id: 'planAndBilling.yourPlan.changePlanFailure.body',
        },
        {
          newLine: <br key={'change-plan-failure'} />,
        },
      )}
      ConfirmButton={
        <PrimaryButton onClick={onClose}>
          <FormattedMessage className="uppercase" id="action.ok" />
        </PrimaryButton>
      }
    />
  )
}

export const RechargeSuccessDialog = ({
  getPlanProp,
  isOpen,
  onClose,
  planType,
  nextPlan,
}) => {
  if (!nextPlan) return null
  return (
    <ConfirmationDialog
      fullWidth
      maxWidth="xs"
      open={isOpen}
      title={
        <span className="text-blue-primary">
          <FormattedMessage id="planAndBilling.yourPlan.changePlanSuccess.title" />
        </span>
      }
      body={
        <div className="text-black">
          <TypographyFormattedMessage
            className="font-bold mb-16"
            id={'planAndBilling.yourPlan.changePlanSuccess.startYourPlanNow'}
          />
          <TypographyFormattedMessage
            className="font-bold"
            id={'planAndBilling.yourPlan.changePlanSuccess.renewAction'}
          />
          <Typography variant="h6">{getPlanProp('name')}</Typography>
          {isPhysicalWebStarterPackByPlanId(getPlanProp('planId')) ? (
            <TypographyFormattedMessage
              id="planAndBilling.physicalWebStarterSubtitle"
              values={{
                price: getPlanProp('price'),
                quantity: getPlanProp('notesQuantity'),
              }}
            />
          ) : (
            <TypographyFormattedMessage
              id="planAndBilling.yourPlan.plan.info.label"
              values={{
                type: planType,
                priceRatio: pricePerNoti(
                  getPlanProp('price'),
                  getPlanProp('notesQuantity'),
                ),
                numberNotifications: getPlanProp('notesQuantity'),
                b: (text) => <b>{text}</b>,
              }}
            />
          )}
          <TypographyFormattedMessage
            id="planAndBilling.yourPlan.changePlan.recurring.label"
            values={{
              price:
                RecurringPlanPrice[getPlanProp('planId')] ||
                getPlanProp('price'),
              type: planType,
              b: (text) => <b>{text}</b>,
            }}
          />
        </div>
      }
      ConfirmButton={
        <PrimaryButton onClick={onClose}>
          <FormattedMessage className="uppercase" id="action.ok" />
        </PrimaryButton>
      }
    />
  )
}

export const PurchaseSuccessDialog = ({
  getPlanProp,
  isOpen,
  onClose,
  planType,
  nextPlan,
}) => {
  const currentPlan = usePlan()
  const subscription = useSubscription()
  const isPlaceExpired = useIsPlaceExpired()
  const refCurrentPlan = useRef(currentPlan)

  if (!nextPlan) return null
  return (
    <ConfirmationDialog
      fullWidth
      maxWidth="xs"
      open={isOpen}
      title={
        <span className="text-blue-primary">
          <FormattedMessage
            id={
              isDowngradePlan({
                currentPlan: refCurrentPlan.current,
                nextPlan,
                isPlaceExpired,
              })
                ? 'planAndBilling.yourPlan.downgrade.successful'
                : 'planAndBilling.yourPlan.changePlanSuccess.title'
            }
          />
        </span>
      }
      body={
        <div className="text-black">
          <TypographyFormattedMessage
            className="font-bold mb-16"
            id={
              isDowngradePlan({
                currentPlan: refCurrentPlan.current,
                nextPlan,
                isPlaceExpired,
              })
                ? 'planAndBilling.yourPlan.changePlanSuccess.startOn'
                : 'planAndBilling.yourPlan.changePlanSuccess.startNow'
            }
            values={{
              date: formatDate(
                R.propOr(moment(), 'nextBillingDate', subscription),
              ),
            }}
          />
          <TypographyFormattedMessage
            className="font-bold"
            id={'planAndBilling.yourPlan.changePlanSuccess.action'}
            values={{
              type: isUpgradePlan({
                currentPlan: refCurrentPlan.current,
                nextPlan,
                isPlaceExpired,
              })
                ? PlanActionType.UPGRADE
                : PlanActionType.DOWNGRADE,
              plan: R.prop('name', refCurrentPlan.current),
            }}
          />
          <Typography variant="h6">{getPlanProp('name')}</Typography>
          {isPhysicalWebStarterPackByPlanId(getPlanProp('planId')) ? (
            <TypographyFormattedMessage
              id="planAndBilling.physicalWebStarterSubtitle"
              values={{
                price: getPlanProp('price'),
                quantity: getPlanProp('notesQuantity'),
              }}
            />
          ) : (
            <TypographyFormattedMessage
              id="planAndBilling.yourPlan.plan.info.label"
              values={{
                type: planType,
                priceRatio: pricePerNoti(
                  getPlanProp('price'),
                  getPlanProp('notesQuantity'),
                ),
                numberNotifications: getPlanProp('notesQuantity'),
                b: (text) => <b>{text}</b>,
              }}
            />
          )}
          <TypographyFormattedMessage
            id="planAndBilling.yourPlan.changePlan.recurring.label"
            values={{
              price:
                RecurringPlanPrice[getPlanProp('planId')] ||
                getPlanProp('price'),
              type: planType,
              b: (text) => <b>{text}</b>,
            }}
          />
        </div>
      }
      ConfirmButton={
        <PrimaryButton onClick={onClose}>
          <FormattedMessage className="uppercase" id="action.ok" />
        </PrimaryButton>
      }
    />
  )
}

const parseAddress = (data) => ({
  ...data,
  fullName: `${R.prop('firstName', data)} ${R.prop('lastName', data)}`,
})

const SummaryPaymentMethod = ({
  paymentMethodNonce,
  isPlanFreeTierByPlanId,
}) => {
  const verifyPaymentMethod = useCallback(() => {
    if (isPlanFreeTierByPlanId) return Promise.resolve({})
    return effects.apis.verifyPaymentMethod({
      paymentMethodNonce: paymentMethodNonce,
    })
  }, [isPlanFreeTierByPlanId, paymentMethodNonce])

  const { values: paymentMethod, isLoading } = useFetcher(
    verifyPaymentMethod,
    {},
  )

  return (
    <div className="w-full md:w-1/2 p-24 p-24">
      <Typography className="font-bold">
        <FormattedMessage id="planAndBilling.paymentMethod.label" />
      </Typography>
      {isPlanFreeTierByPlanId ? (
        <Typography>
          <FormattedMessage id="planAndBilling.yourPlan.paymentSummary.notRequired" />
        </Typography>
      ) : (
        <Loader
          isLoading={isLoading}
          placeholder={
            <FuseAnimate
              enter={{ animation: 'fadeIn' }}
              leave={{ animation: 'fadeOut' }}
            >
              <CircularProgress className="self-center m-16" />
            </FuseAnimate>
          }
        >
          {() =>
            !R.isEmpty(paymentMethod) && (
              <PlanBillingPaymentMethodCard
                paymentMethod={paymentMethod}
                hiddenExpires
              />
            )
          }
        </Loader>
      )}
    </div>
  )
}

function PlanBillingChangePlanSummary(props) {
  const { isOpen, onClose, onPurchase, data, subscription } = props
  const { refetch } = usePlanBillingContext()
  const isPlaceExpired = useIsPlaceExpired()

  const getPlanProp = (prop) => R.pathOr('', ['plan', prop], data)
  const { formatMessage } = useIntl()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPurchaseSuccess, setIsPurchaseSuccess] = useState()
  const currentPlan = usePlan()

  const handlePurchase = async () => {
    setIsSubmitting(true)
    try {
      await onPurchase()
      setIsPurchaseSuccess(true)
    } catch (error) {
      setIsPurchaseSuccess(false)
    } finally {
      onClose()
      setIsSubmitting(false)
    }
  }

  const closeResultDialog = () => {
    setIsPurchaseSuccess(null)
    refetch()
  }

  const planType = getPlanType(getPlanProp('planId'))
  const classes = useStyles()

  return (
    <>
      <ResponsiveDialog
        open={isOpen}
        scroll="paper"
        fullWidth
        maxWidth="md"
        aria-labelledby="scroll-dialog-title"
        classes={{ paper: 'overflow-y-visible' }}
      >
        <DialogTitle id="alert-dialog-title">
          <div className="flex justify-between items-center">
            <Typography variant="h6">
              <FormattedMessage id="planAndBilling.yourPlan.paymentSummary.dialog.title" />
            </Typography>
            <IconButton disableRipple disabled={isSubmitting} onClick={onClose}>
              <Icon>close</Icon>
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="p-0">
          <div className={clsx(classes.summaryBodyContainer)}>
            <div className="flex flex-col">
              <Typography className="font-bold px-24 pt-24">
                <FormattedMessage id="planAndBilling.yourPlan.paymentSummary.yourPlan" />
              </Typography>
              <div className="flex flex-col sm:flex-row">
                <div className="flex-1 p-24">
                  <Typography variant="h6">{getPlanProp('name')}</Typography>
                  <SummaryPlanInformation
                    planType={planType}
                    getPlanProp={getPlanProp}
                    currentPlan={currentPlan}
                    data={data}
                    isPlaceExpired={isPlaceExpired}
                    subscription={subscription}
                  />
                </div>
                {isPhysicalWebStarterPackByPlanId(getPlanProp('planId')) && (
                  <div className="flex-1 p-24 space-y-3">
                    <Typography className="font-bold capitalize">
                      <FormattedMessage id="planAndBilling.ShippingAddress.label" />
                    </Typography>
                    <PlaceDetailsInformation
                      billingAddress={parseAddress(data.shippingAddress)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row">
            <div className="w-full md:w-1/2 p-24 border-b-1 md:border-b-0 border-grey-light space-y-3">
              <Typography className="font-bold">
                <FormattedMessage id="planAndBilling.billingDetails.label" />
              </Typography>
              {R.prop('address1', data) ? (
                <PlaceDetailsInformation billingAddress={parseAddress(data)} />
              ) : (
                '-'
              )}
            </div>
            <SummaryPaymentMethod
              paymentMethodNonce={data.paymentMethodNonce}
              isPlanFreeTierByPlanId={isPlanFreeTierByPlanId(
                getPlanProp('planId'),
              )}
            />
          </div>
        </DialogContent>
        <DialogActions className="justify-end px-24">
          <SecondaryButton
            disabled={isSubmitting}
            labelId="action.back"
            onClick={onClose}
          />
          <PrimaryButton
            isLoading={isSubmitting}
            className="ltr:ml-24 rtl:mr-24"
            disabled={isSubmitting}
            onClick={handlePurchase}
            labelId="action.purchase"
          />
        </DialogActions>
      </ResponsiveDialog>
      <PurchaseSuccessDialog
        isOpen={R.equals(true, isPurchaseSuccess)}
        formatMessage={formatMessage}
        onClose={closeResultDialog}
        getPlanProp={getPlanProp}
        planType={planType}
        nextPlan={R.prop('plan', data)}
      />
      <PurchaseFailureDialog
        formatMessage={formatMessage}
        isOpen={R.equals(false, isPurchaseSuccess)}
        onClose={closeResultDialog}
      />
    </>
  )
}

export default PlanBillingChangePlanSummary

const SummaryPlanInformation = ({
  getPlanProp,
  planType,
  currentPlan,
  data,
  subscription,
  isPlaceExpired,
}) => {
  if (isPhysicalWebStarterPackByPlanId(getPlanProp('planId'))) {
    return (
      <>
        <PlanPhysicalWebStarterPackInfo
          className="mb-12"
          plan={R.prop('plan', data)}
        />
        <Typography className="mb-16">
          <FormattedMessage
            id="planAndBilling.yourPlan.changePlan.recurring.label"
            values={{
              type: planType,
              price:
                RecurringPlanPrice[getPlanProp('planId')] ||
                getPlanProp('price'),
            }}
          />
        </Typography>
        <Typography>
          <FormattedMessage
            id="planAndBilling.yourPlan.paymentSummary.nextDue"
            values={{
              date: getExpireDatePlanByPlanType(planType),
            }}
          />
        </Typography>
      </>
    )
  }
  return (
    <>
      <Typography className="mt-12">
        <FormattedMessage
          id={'planAndBilling.yourPlan.plan.info.label'}
          values={{
            numberNotifications: getPlanProp('notesQuantity'),
            type: planType,
            priceRatio: pricePerNoti(
              getPlanProp('price'),
              getPlanProp('notesQuantity'),
            ),
            b: (text) => <b>{text}</b>,
          }}
        />
      </Typography>
      <Typography>
        <FormattedMessage
          id="planAndBilling.yourPlan.plan.info.price.label"
          values={{
            type: planType,
            price: getPlanProp('price'),
          }}
        />
      </Typography>
      <Typography className="mt-24">
        <FormattedMessage
          id="planAndBilling.yourPlan.plan.nextDue.label"
          values={{
            date: isUpgradePlan({
              currentPlan,
              nextPlan: R.prop('plan', data),
              isPlaceExpired,
            })
              ? getExpireDatePlanByPlanType(planType)
              : formatDate(R.propOr(moment(), 'nextBillingDate', subscription)),
          }}
        />
      </Typography>
    </>
  )
}
